<template>
  <div class="relative">
    <button
        ref="triggerButton"
        @click.stop="handleToggle"
        class="p-1 text-zinc-600 hover:bg-gray-100 rounded-lg transition-colors"
        :title="__('Actions')"
    >
      <Icon name="more_horiz" class="w-5 h-5 text-xl" />
    </button>

    <Teleport to="body">
      <transition
          enter-active-class="transition-all duration-150 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition-all duration-100 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
      >
        <div
            v-if="isOpen"
            v-click-outside="handleClickOutside"
            class="fixed z-[9999] rounded-lg border bg-white shadow-lg"
            :style="popupStyle"
            data-popup-menu
        >
          <ul class="flex flex-col text-sm text-gray-700">
            <li>
              <button
                  @click.stop="$emit('reviewSubmission', submissionId)"
                  class="block w-full cursor-pointer rounded px-3.5 py-2 text-left transition-colors duration-150 hover:bg-gray-100"
              >
                {{ __('Review') }}
              </button>
            </li>

            <li :key="`pipeline-${submissionId}`">
              <div class="relative">
                <button
                    v-if="!isMobile"
                    @mouseenter="showPipelinePopup(submissionId)"
                    @mouseleave="hidePipelinePopup()"
                    class="relative z-30 flex w-full cursor-pointer items-center gap-x-2 rounded px-3.5 py-2 transition-colors duration-150 hover:bg-gray-100"
                >
                  <Icon name="arrow_back_ios" class="w-3 h-3 text-base text-zinc-600 mr-1" />
                  <span class="whitespace-nowrap">
                    {{ __('Add to Pipeline') }}
                  </span>
                </button>

                <button
                    v-else
                    @click.stop="emit('mobileAction', submissionId, 'pipeline'); emit('toggle')"
                    class="relative z-30 flex w-full cursor-pointer items-center gap-x-2 rounded px-3.5 py-2 transition-colors duration-150 hover:bg-gray-100"
                >
                  <Icon name="arrow_back_ios" class="w-3 h-3 text-base text-zinc-600 mr-1" />
                  <span class="whitespace-nowrap">
                    {{ __('Add to Pipeline') }}
                  </span>
                </button>

                <ChallengeSubmissionPipelinePopup
                    v-if="!isMobile"
                    :submissionId="submissionId"
                    :options="pipelineOptions"
                    :is-open="hoveredPipelineItem === submissionId"
                    :is-loading="isLoadingPipeline"
                    :position-from-end="positionFromEnd"
                    @addToPipeline="
                    $emit('addToPipeline', $event);
                    hidePipelinePopup();
                  "
                    @mouseenter="showPipelinePopup(submissionId)"
                    @mouseleave="hidePipelinePopup()"
                    @click.stop="
                    hoveredPipelineItem === submissionId
                      ? (hoveredPipelineItem = null)
                      : (hoveredPipelineItem = submissionId)
                  "
                />
              </div>
            </li>

            <li :key="`job-${submissionId}`">
              <div class="relative">
                <button
                    v-if="!isMobile"
                    @mouseenter="showJobPopup(submissionId)"
                    @mouseleave="hideJobPopup()"
                    class="relative z-30 flex w-full cursor-pointer items-center gap-x-2 rounded px-3.5 py-2 transition-colors duration-150 hover:bg-gray-100"
                >
                  <Icon name="arrow_back_ios" class="w-3 h-3 text-base text-zinc-600 mr-1" />
                  <span class="whitespace-nowrap">{{ __('Invite to Job') }}</span>
                </button>

                <button
                    v-else
                    @click.stop="emit('mobileAction', submissionId, 'job'); emit('toggle')"
                    class="relative z-30 flex w-full cursor-pointer items-center gap-x-2 rounded px-3.5 py-2 transition-colors duration-150 hover:bg-gray-100"
                >
                  <Icon name="arrow_back_ios" class="w-3 h-3 text-base text-zinc-600 mr-1" />
                  <span class="whitespace-nowrap">{{ __('Invite to Job') }}</span>
                </button>

                <ChallengeSubmissionJobPopup
                    v-if="!isMobile"
                    :submissionId="submissionId"
                    :options="jobOptions"
                    :is-open="hoveredJobItem === submissionId"
                    :position-from-end="positionFromEnd"
                    :is-loading="isLoadingJob"
                    @inviteToJob="
                    $emit('inviteToJob', $event);
                    hideJobPopup();
                  "
                    @mouseenter="showJobPopup(submissionId)"
                    @mouseleave="hideJobPopup()"
                    @click.stop="
                    hoveredJobItem === submissionId
                      ? (hoveredJobItem = null)
                      : (hoveredJobItem = submissionId)
                  "
                />
              </div>
            </li>
          </ul>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
import {ref, computed, watch, nextTick, onMounted, onBeforeUnmount} from 'vue';
import { __ } from '@/helpers';
import { Icon } from '@/components/block/icon';
import ChallengeSubmissionPipelinePopup from './ChallengeSubmissionPipelinePopup.vue';
import ChallengeSubmissionJobPopup from './ChallengeSubmissionJobPopup.vue';

const hoveredPipelineItem = ref(null);
const hoveredJobItem = ref(null);
const hoverTimeout = ref(null);
const triggerButton = ref(null);
const popupStyle = ref({});

const emit = defineEmits([
  'toggle',
  'reviewSubmission',
  'addToPipeline',
  'inviteToJob',
  'mobileAction'
]);

const props = defineProps({
  submissionId: {
    type: Number,
    required: true
  },
  isOpen: {
    type: Boolean,
    required: false
  },
  pipelineOptions: {
    type: Array,
    required: false,
    default: () => []
  },
  jobOptions: {
    type: Array,
    required: false,
    default: () => []
  },
  isLoadingPipeline: {
    type: Boolean,
    required: false
  },
  isLoadingJob: {
    type: Boolean,
    required: false
  },
  positionFromEnd: {
    type: Number,
    required: false
  }
});


const windowWidth = ref(window.innerWidth);
const isMobile = computed(() => windowWidth.value < 640)

const handleResize = () => {
  windowWidth.value = window.innerWidth;
}

onMounted(() => {
  window.addEventListener("resize", handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize)
})

const handleToggle = () => {
  emit('toggle');
};

const handleClickOutside = () => {
  if (props.isOpen) {
    emit('toggle');
  }
};

const calculatePosition = () => {
  if (!triggerButton.value) return {};

  const rect = triggerButton.value.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;

  const openAbove = spaceBelow < 200 && spaceAbove > spaceBelow;

  if (openAbove) {
    return {
      top: `${rect.top - 130}px`,
      left: `${rect.right - 200}px`,
    };
  }

  return {
    top: `${rect.bottom + 8}px`,
    left: `${rect.right - 200}px`,
  };
};

watch(() => props.isOpen, async (isOpen) => {
  if (isOpen) {
    await nextTick();
    popupStyle.value = calculatePosition();
  }
});

const showPipelinePopup = (itemId) => {
  if (hoveredJobItem.value === itemId) {
    hoveredJobItem.value = null;
  }
  clearTimeout(hoverTimeout.value);
  hoveredPipelineItem.value = itemId;
};

const hidePipelinePopup = () => {
  hoverTimeout.value = setTimeout(() => {
    hoveredPipelineItem.value = null;
  }, 200);
};

const showJobPopup = (itemId) => {
  if (hoveredPipelineItem.value === itemId) {
    hoveredPipelineItem.value = null;
  }
  clearTimeout(hoverTimeout.value);
  hoveredJobItem.value = itemId;
};

const hideJobPopup = () => {
  hoverTimeout.value = setTimeout(() => {
    hoveredJobItem.value = null;
  }, 200);
};

const vClickOutside = {
  mounted(el, binding) {
    el.clickOutsideEvent = function(event) {
      if (!(el === event.target || el.contains(event.target)) &&
          !triggerButton.value?.contains(event.target)) {
        binding.value();
      }
    };
    document.addEventListener('click', el.clickOutsideEvent);
  },
  unmounted(el) {
    document.removeEventListener('click', el.clickOutsideEvent);
  },
};
</script>